/**
 * Icon upload widget for a Dataset / Category / Dashboard.
 *
 * Reads the current icon from the parent payload and POSTs a new upload
 * to the resource's `/upload_icon/` endpoint (backed by IconUploadMixin).
 * Resolves images via data-URL, so no second fetch is needed after upload.
 */
import { useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api/client';

type ResourceKind = 'datasets' | 'categories' | 'dashboards';

interface IconUploadFieldProps {
  resource: ResourceKind;
  id: number | null | undefined;
  /** base64-encoded content (from a previous upload). */
  initialContent?: string;
  /** file extension without dot: png / svg / webp … */
  initialExtension?: string;
  onChange?: (content: string, extension: string) => void;
}

function dataUrl(content: string, extension: string): string {
  const ext = (extension || 'png').toLowerCase();
  return `data:image/${ext === 'svg' ? 'svg+xml' : ext};base64,${content}`;
}

export function IconUploadField({
  resource,
  id,
  initialContent,
  initialExtension,
  onChange,
}: IconUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [content, setContent] = useState(initialContent || '');
  const [extension, setExtension] = useState(initialExtension || '');
  const [busy, setBusy] = useState(false);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<{
        data_url: string;
        icon_extension: string;
      }>(`/reporting/${resource}/${id}/upload_icon/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const dataUrlResult = response.data.data_url || '';
      const base64 = dataUrlResult.split(',')[1] || '';
      setContent(base64);
      setExtension(response.data.icon_extension || '');
      onChange?.(base64, response.data.icon_extension || '');
      toast.success('Icon uploaded');
    } catch (err) {
      console.error('Icon upload failed:', err);
      toast.error('Icon upload failed');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await api.delete(`/reporting/${resource}/${id}/clear_icon/`);
      setContent('');
      setExtension('');
      onChange?.('', '');
    } catch (err) {
      console.error('Icon clear failed:', err);
      toast.error('Could not clear icon');
    } finally {
      setBusy(false);
    }
  };

  const preview = content && extension ? dataUrl(content, extension) : '';

  return (
    <div className="flex items-center gap-3">
      <div className="h-14 w-14 rounded border bg-muted/50 flex items-center justify-center overflow-hidden shrink-0">
        {preview ? (
          <img src={preview} alt="Icon" className="h-full w-full object-contain" />
        ) : (
          <span className="text-[10px] text-muted-foreground">No icon</span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.gif,.svg,.webp"
          className="hidden"
          onChange={(e) => void handleFileSelected(e)}
        />
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePickFile}
            disabled={busy || !id}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
            {preview ? 'Replace' : 'Upload'}
          </Button>
          {preview && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void handleClear()}
              disabled={busy}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1 text-destructive" />
              Clear
            </Button>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          PNG / JPG / SVG / WebP, max 512&nbsp;KB.
        </span>
      </div>
    </div>
  );
}
